import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/app/modules/shared/services/local.storage.service';
import { IG_SHADOW_PREFLIGHT_CSS } from './preflight.css';
import { IG_SUN_EDITOR_CONTENT_CSS } from './sun.editor.content.css';

@Injectable({
  providedIn: 'root',
})
export class ShadowStyleLoaderService {
  constructor(private localStorageService: LocalStorageService) {}

  public injectBaseStyles(shadowRoot: ShadowRoot | null, onStylesLoaded?: () => void): void {
    if (!shadowRoot) {
      return;
    }

    const styleUrl = this.getStylesUrl();
    this.appendInlineStyle(shadowRoot, 'ig-preflight', IG_SHADOW_PREFLIGHT_CSS);
    this.appendInlineStyle(shadowRoot, 'ig-sun-editor-content', IG_SUN_EDITOR_CONTENT_CSS);

    if (styleUrl) {
      this.appendStylesheet(shadowRoot, styleUrl, onStylesLoaded);
    } else if (onStylesLoaded) {
      onStylesLoaded();
    }
  }

  public injectGlobalPreflight(): HTMLStyleElement | null {
    return this.appendDocumentStyle('ig-preflight-global', IG_SHADOW_PREFLIGHT_CSS);
  }

  public appendStylesheet(
    shadowRoot: ShadowRoot,
    href: string,
    onLoad?: () => void
  ): HTMLLinkElement | null {
    if (!href) {
      return null;
    }

    const existing = Array.from(shadowRoot.querySelectorAll('link[rel="stylesheet"]')).find(
      (link) => (link as HTMLLinkElement).href === this.toAbsoluteUrl(href)
    ) as HTMLLinkElement | undefined;

    if (existing) {
      if (onLoad) {
        onLoad();
      }
      return existing;
    }

    const link = document.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('href', href);

    if (onLoad) {
      link.onload = () => onLoad();
    }

    shadowRoot.appendChild(link);
    return link;
  }

  public appendInlineStyle(
    shadowRoot: ShadowRoot,
    styleId: string,
    cssText: string
  ): HTMLStyleElement | null {
    if (!cssText) {
      return null;
    }

    const existing = shadowRoot.querySelector(
      `style[data-ig-style="${styleId}"]`
    ) as HTMLStyleElement | null;

    if (existing) {
      return existing;
    }

    const style = document.createElement('style');
    style.setAttribute('data-ig-style', styleId);
    style.textContent = cssText;
    shadowRoot.appendChild(style);
    return style;
  }

  public appendDocumentStyle(styleId: string, cssText: string): HTMLStyleElement | null {
    if (!cssText) {
      return null;
    }

    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) {
      return null;
    }

    const existing = head.querySelector(
      `style[data-ig-style="${styleId}"]`
    ) as HTMLStyleElement | null;

    if (existing) {
      return existing;
    }

    const style = document.createElement('style');
    style.setAttribute('data-ig-style', styleId);
    style.textContent = cssText;
    head.appendChild(style);
    return style;
  }

  public getStylesUrl(): string {
    return (
      this.localStorageService.getItem<string>('STYLES_URL') ||
      'https://localhost:4201/styles.css'
    );
  }

  private toAbsoluteUrl(url: string): string {
    try {
      return new URL(url, window.location.href).toString();
    } catch (error) {
      return url;
    }
  }
}
